import i18n, { TFunction } from 'i18next';
import { getCountryLabelFromCountries, localizeCountryInCompositeLabel } from 'i18n/countryLabel';
import moment from 'moment';
import { Provider } from 'store/api/enums';
import { DataLogStatus, EntityStatus } from 'store/app/enums';
import { User } from 'store/auth/models';
import { Country } from 'store/common/models';
import { Mapping, Organisation } from 'store/organisation/models';
import { MappedReturn } from 'store/returns/models/returnData';
import { VatReturnLog } from 'store/vat/models/vatReturnLog';
import { getProviderName } from 'utils/get-provider-name';

const LIST_SEPARATOR = '; ';

const formatDate = (value: string | number | Date | null | undefined): string => {
  if (value == null || value === '') return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

const formatBoolean = (value: boolean | undefined, t: TFunction): string => {
  if (value === undefined) return '';
  return value ? t('common.yes') : t('common.no');
};

const joinList = (items: (string | null | undefined)[]): string =>
  items
    .filter((item) => item != null && String(item).trim() !== '')
    .join(LIST_SEPARATOR);

const formatUserName = (user?: User | null): string => {
  if (!user) return '';
  return (
    user.fullName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    ''
  );
};

const formatUserEmail = (user?: User | null): string => user?.email || '';

const getPeriodDisplay = (record: MappedReturn): string => {
  const isNotStarted = record.status === DataLogStatus.NOT_STARTED;
  const hasDates = !!record.fromReportingDate || !!record.toReportingDate;

  if (isNotStarted && !hasDates) {
    return record.nextPeriod || '';
  }

  const from = record.fromReportingDate
    ? moment(record.fromReportingDate).format('DD/MM/YY')
    : '';
  const to = record.toReportingDate
    ? moment(record.toReportingDate).format('DD/MM/YY')
    : '';

  if (!from && !to) return '';
  if (!from) return to;
  if (!to) return from;
  return `${from} - ${to}`;
};

const getEntityStatusLabel = (
  status: EntityStatus | undefined,
  t: TFunction
): string => {
  if (status === EntityStatus.VIEW_ONLY) {
    return t('organisation.export.status-view-only');
  }
  if (status === EntityStatus.ACTIVE) {
    return t('common.active');
  }
  return status != null ? String(status) : '';
};

const getConnectionHealthLabel = (
  record: MappedReturn,
  org: Organisation | undefined,
  t: TFunction
): string => {
  if (record.entityStatus === EntityStatus.VIEW_ONLY) {
    return t('organisation.export.status-view-only');
  }
  if (
    org?.isGroup ||
    record.providerName?.toLowerCase() === Provider.GROUP.toLowerCase()
  ) {
    return t('main.connected');
  }
  if (record.connectedProperly) {
    return t('main.connected');
  }
  if (record.providerName?.toLowerCase() === Provider.EXCEL.toLowerCase()) {
    return getEntityStatusLabel(record.entityStatus, t);
  }
  return t('organisation.export.connection-reconnect-required');
};

const getGroupMemberNames = (
  record: MappedReturn,
  organisations: Organisation[] | undefined
): string => {
  if (!record.groupedEntityIds?.length) return '';
  return joinList(
    record.groupedEntityIds.map((id) => {
      const org = organisations?.find((o) => o.uuid === id);
      return org?.friendlyName || id;
    })
  );
};

const getDataLogField = (
  dataLog: VatReturnLog | undefined,
  field: keyof VatReturnLog
): string => {
  const value = dataLog?.[field];
  if (value == null || value === '') return '';
  if (typeof value === 'number') return String(value);
  return String(value);
};

export async function exportReturnsToExcel(
  mappedReturns: MappedReturn[],
  organisations: Organisation[] | undefined,
  mappings: Mapping[] | undefined,
  countries: Country[],
  t: TFunction
): Promise<void> {
  const XLSX = await import('xlsx');

  const columns = {
    taxReturn: t('returns.mapping.tax-return'),
    entityName: t('organisation.company-name'),
    legalName: t('organisation.export.column-legal-name'),
    alternateName: t('organisation.alternate-name'),
    country: t('common.country'),
    connectionType: t('organisation.export.column-connection-type'),
    providerShortCode: t('organisation.export.column-provider-short-code'),
    internalEntityCode: t('organisation.code'),
    vatNumber: t('organisation.export.column-tax-number'),
    entityCurrency: t('returns.export.column-entity-currency'),
    returnFrequency: t('organisation.return-frequency'),
    nextPeriod: t('organisation.next-period'),
    reportingPeriod: t('returns.export.column-reporting-period'),
    nextDueDate: t('organisation.next-due-date'),
    periodFromDate: t('returns.export.column-period-from-date'),
    submissionStatus: t('returns.export.column-submission-status'),
    entityStatus: t('returns.export.column-entity-status'),
    connection: t('organisation.connection-status'),
    cashAccounting: t('organisation.cash-accounting'),
    canDelete: t('returns.export.column-can-delete'),
    preparer: t('main.preparer'),
    preparerEmail: t('returns.export.column-preparer-email'),
    reviewer: t('main.reviewer'),
    reviewerEmail: t('returns.export.column-reviewer-email'),
    client: t('main.client'),
    clientEmail: t('returns.export.column-client-email'),
    dataLogId: t('returns.export.column-data-log-id'),
    generatedDate: t('organisation.generated-date'),
    submissionDate: t('returns.export.column-submission-date'),
    lastUpdatedDate: t('returns.export.column-last-updated-date'),
    dataLogFromDate: t('returns.export.column-data-log-from-date'),
    dataLogToDate: t('returns.export.column-data-log-to-date'),
    dataLogDueDate: t('returns.export.column-data-log-due-date'),
    dataLogCurrency: t('returns.export.column-data-log-currency'),
    transactionsCount: t('returns.export.column-transactions-count'),
    exceptionsCount: t('returns.export.column-exceptions-count'),
    othersCount: t('returns.export.column-others-count'),
    totalTaxTransactions: t('returns.export.column-total-tax-transactions'),
    totalTaxExceptions: t('returns.export.column-total-tax-exceptions'),
    totalTaxOthers: t('returns.export.column-total-tax-others'),
    submittedBy: t('returns.export.column-submitted-by'),
    approvedBy: t('returns.export.column-approved-by'),
    adjustStatus: t('returns.export.column-adjust-status'),
    manualJournalId: t('returns.export.column-manual-journal-id'),
    mappingCreatedDate: t('returns.export.column-mapping-created-date'),
    mappingCreatedBy: t('returns.export.column-mapping-created-by'),
    mappingReturnDueDate: t('returns.export.column-mapping-return-due-date'),
    hmrcConnected: t('returns.export.column-hmrc-connected'),
    hmrcConnectionLinked: t('organisation.export.column-hmrc-connection-linked'),
    isVatGroup: t('organisation.export.column-is-group'),
    groupMembers: t('organisation.export.column-group-members'),
    entityCreatedOn: t('returns.export.column-entity-created-on'),
    entityCreatedBy: t('returns.export.column-entity-created-by'),
    contactPersonName: t('organisation.contact-person-name'),
    contactPersonEmail: t('organisation.contact-person-email'),
    contactPersonTelephone: t('organisation.contact-person-telephone'),
    registeredAddress: t('organisation.registered-address'),
    notes: t('organisation.notes'),
    returnTypeId: t('returns.export.column-return-type-id'),
    mappingId: t('returns.export.column-mapping-id'),
    organisationId: t('organisation.export.column-organisation-id'),
    connectionId: t('organisation.export.column-connection-id'),
  };

  const exportRows = mappedReturns
    .filter(
      (record) =>
        record.providerName?.toLowerCase() !== Provider.CIN7.toLowerCase()
    )
    .map((record) => {
      const org = organisations?.find((o) => o.uuid === record.organisationId);
      const mapping = mappings?.find((m) => m.mappingId === record.mappingId);
      const details = org?.organisationDetails;
      const dataLog = record.dataLog;
      const countryName = getCountryLabelFromCountries(
        record.country,
        countries,
        i18n.language,
        countries.find((c) => c.iso === record.country)?.name ||
          record.country ||
          ''
      );
      const isGroup =
        org?.isGroup ||
        record.providerName?.toLowerCase() === Provider.GROUP.toLowerCase();

      const preparerUser = dataLog?.preparerUser ?? record.preparer;
      const reviewerUser = dataLog?.reviewerUser ?? record.reviewer;
      const clientUser = dataLog?.clientUser ?? record.client;

      return {
        [columns.taxReturn]: record.mappingName
          ? localizeCountryInCompositeLabel(
              record.mappingName,
              countries,
              i18n.language
            )
          : '',
        [columns.entityName]: record.organisation || org?.friendlyName || '',
        [columns.legalName]: org?.legalName || '',
        [columns.alternateName]: details?.alternateName || '',
        [columns.country]: countryName,
        [columns.connectionType]: getProviderName(
          record.providerName || org?.providerName
        ),
        [columns.providerShortCode]: record.shortCode || org?.shortCode || '',
        [columns.internalEntityCode]: details?.code || '',
        [columns.vatNumber]: record.vatNumber || org?.taxNumber || '',
        [columns.entityCurrency]: org?.currency || dataLog?.currency || '',
        [columns.returnFrequency]:
          record.returnFrequency != null
            ? String(record.returnFrequency)
            : mapping?.returnFrequency != null
              ? String(mapping.returnFrequency)
              : '',
        [columns.nextPeriod]: record.nextPeriod || mapping?.nextPeriod || '',
        [columns.reportingPeriod]: getPeriodDisplay(record),
        [columns.nextDueDate]: formatDate(record.nextDueDate),
        [columns.periodFromDate]: formatDate(record.periodFromDate),
        [columns.submissionStatus]: record.status || '',
        [columns.entityStatus]: getEntityStatusLabel(record.entityStatus, t),
        [columns.connection]: getConnectionHealthLabel(record, org, t),
        [columns.cashAccounting]: formatBoolean(
          mapping?.cashAccounting,
          t
        ),
        [columns.canDelete]: formatBoolean(record.canDelete, t),
        [columns.preparer]: formatUserName(preparerUser),
        [columns.preparerEmail]: formatUserEmail(preparerUser),
        [columns.reviewer]: formatUserName(reviewerUser),
        [columns.reviewerEmail]: formatUserEmail(reviewerUser),
        [columns.client]: formatUserName(clientUser),
        [columns.clientEmail]: formatUserEmail(clientUser),
        [columns.dataLogId]: dataLog?.uuid || '',
        [columns.generatedDate]: formatDate(dataLog?.generatedDate),
        [columns.submissionDate]: formatDate(dataLog?.submissionDate),
        [columns.lastUpdatedDate]: formatDate(dataLog?.lastUpdatedDate),
        [columns.dataLogFromDate]: formatDate(
          dataLog?.fromReportingDate ?? record.fromReportingDate
        ),
        [columns.dataLogToDate]: formatDate(
          dataLog?.toReportingDate ?? record.toReportingDate
        ),
        [columns.dataLogDueDate]: formatDate(dataLog?.dueDate),
        [columns.dataLogCurrency]: dataLog?.currency || '',
        [columns.transactionsCount]: getDataLogField(
          dataLog,
          'transactionsCount'
        ),
        [columns.exceptionsCount]: getDataLogField(dataLog, 'exceptionsCount'),
        [columns.othersCount]: getDataLogField(dataLog, 'othersCount'),
        [columns.totalTaxTransactions]: getDataLogField(
          dataLog,
          'totalTaxTransactions'
        ),
        [columns.totalTaxExceptions]: getDataLogField(
          dataLog,
          'totalTaxExceptions'
        ),
        [columns.totalTaxOthers]: getDataLogField(dataLog, 'totalTaxOthers'),
        [columns.submittedBy]:
          dataLog?.submittedByUser?.email ||
          dataLog?.submittedBy ||
          getDataLogField(dataLog, 'submittedByUserId'),
        [columns.approvedBy]: dataLog?.approvedBy || '',
        [columns.adjustStatus]: dataLog?.adjustStatus || '',
        [columns.manualJournalId]: dataLog?.manualJournalId || '',
        [columns.mappingCreatedDate]: formatDate(mapping?.createdDate),
        [columns.mappingCreatedBy]: mapping?.createdBy || '',
        [columns.mappingReturnDueDate]: formatDate(mapping?.returnDueDate),
        [columns.hmrcConnected]: formatBoolean(mapping?.hmrcConnected, t),
        [columns.hmrcConnectionLinked]: formatBoolean(
          Boolean(record.hmrcConnectionUuid || org?.hmrcConnectionUuid),
          t
        ),
        [columns.isVatGroup]: formatBoolean(isGroup, t),
        [columns.groupMembers]: getGroupMemberNames(record, organisations),
        [columns.entityCreatedOn]: formatDate(org?.createTimestamp),
        [columns.entityCreatedBy]: org?.creator || '',
        [columns.contactPersonName]: details?.contactPersonName || '',
        [columns.contactPersonEmail]: details?.contactPersonEmail || '',
        [columns.contactPersonTelephone]: details?.contactPersonTelephone || '',
        [columns.registeredAddress]: details?.registeredAddress || '',
        [columns.notes]: details?.notes || '',
        [columns.returnTypeId]: record.returnTypeId || mapping?.returnTypeId || '',
        [columns.mappingId]: record.mappingId || '',
        [columns.organisationId]: record.organisationId || '',
        [columns.connectionId]: record.connectionId || '',
      };
    });

  const headers = Object.values(columns);
  const ws = XLSX.utils.json_to_sheet(exportRows, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, t('navigation.returns'));
  XLSX.writeFile(wb, `returns-${moment().format('YYYY-MM-DD')}.xlsx`);
}

